import { useMemo, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Loader2, Trash2, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ProductForm, ProductFormData } from '@/components/products';
import { useProducerProducts } from '@/hooks/useProducerProducts';
import { useProducerById } from '@/hooks/useMyProducers';
import { useToast } from '@/hooks/use-toast';
import { isPending } from '@/lib/producer/status';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ProducerLayout } from '@/components/producer/ProducerLayout';
import { ShareStudioSheet } from '@/features/share/ShareStudioSheet';
import { EntityShareAction } from '@/features/share/EntityShareAction';
import { mapProductToShareTarget } from '@/features/share/mappers/mapProductToShareTarget';
import { producerProductPath, productEmbedPath } from '@/lib/paths';
import { features } from '@/config/features';
import { useProductHeroImage } from '@/hooks/useProductHeroImage';

export default function ProducerProductEdit() {
  const { farmId, productId } = useParams<{ farmId: string; productId: string }>();
  const navigate = useNavigate();
  const { producer, isLoading: producerLoading } = useProducerById(farmId);
  const { products, loading, updateProduct, softDeleteProduct, deleting } = useProducerProducts(farmId);
  const { toast } = useToast();

  const [isSaving, setIsSaving] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [showShareStudio, setShowShareStudio] = useState(false);

  const basePath = `/producer/farm/${farmId}/shop`;
  const product = products.find((p) => p.id === productId);

  // Must be called unconditionally on every render to preserve hook order.
  // The hook should handle missing/undefined productId internally.
  const { data: productCoverUrl } = useProductHeroImage(productId, 640);

  const handleSave = async (data: ProductFormData, _publish: boolean) => {
    if (!data.title || !productId) {
      toast({
        title: 'Titel krävs',
        description: 'Ange en titel för produkten.',
        variant: 'destructive',
      });
      return;
    }

    setIsSaving(true);
    try {
      const priceOre = Math.round(parseFloat(data.priceKr || '0') * 100);

      await updateProduct({
        id: productId,
        title: data.title,
        description: data.description || undefined,
        price_ore: priceOre,
        status: data.status,
        product_type: data.productType,
        bundle_items: data.productType === 'bundle' ? data.bundleItems : undefined,
        seo_title: data.seoTitle || null,
        seo_description: data.seoDescription || null,
        seo_image_url: data.seoImageUrl || null,
        pickup_instructions: data.pickupInstructions || null,
        stock_limit: data.stockLimit,
      });

      toast({
        title: 'Ändringar sparade',
        description:
          data.status === 'public'
            ? 'Produkten är nu publicerad och synlig för kunder.'
            : 'Produkten har uppdaterats.',
      });

      navigate(`${basePath}/products`);
    } catch (err: any) {
      const errorMsg = err?.message || '';
      const isRls = errorMsg.includes('row-level security') || errorMsg.includes('unauthorized');

      toast({
        title: isRls ? 'Behörighet saknas' : 'Kunde inte spara',
        description: isRls
          ? 'Kontrollera att du är medlem i gården och försök igen.'
          : `Det gick inte att spara just nu. Försök igen om en stund.${
              import.meta.env.DEV ? ` (${errorMsg})` : ''
            }`,
        variant: 'destructive',
      });

      console.error('[product-edit] Save failed:', { error: err, productId });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!productId || deleteConfirmText !== 'RADERA') return;

    try {
      await softDeleteProduct(productId);
      toast({
        title: 'Produkten raderad',
        description: 'Produkten har tagits bort och kan inte längre köpas.',
      });
      navigate(`${basePath}/products`);
    } catch {
      toast({
        title: 'Kunde inte radera',
        description: 'Det gick inte att radera just nu. Försök igen om en stund.',
        variant: 'destructive',
      });
    } finally {
      setDeleteDialogOpen(false);
      setDeleteConfirmText('');
    }
  };

  const handleCancel = () => navigate(`${basePath}/products`);

  const isDeleting = deleting === productId;
  const isPublishBlocked = producer ? isPending(producer.producerStatus) : false;

  const shareTarget =
    product && producer
      ? mapProductToShareTarget({
          product,
          coverImageUrl: productCoverUrl,
          producerName: producer.name || '',
          producerSlug: producer.slug || '',
          producerId: producer.id,
        })
      : null;

  const productPublicUrl =
    product && producer?.slug
      ? `${window.location.origin}${producerProductPath(producer.slug, product.id)}`
      : null;

  const productEmbedSnippet = useMemo(() => {
    if (!product?.id) return null;
    const src = `${window.location.origin}${productEmbedPath(product.id)}`;
    return `<iframe\n  src="${src}"\n  width="100%"\n  height="420"\n  style="border:0;"\n  loading="lazy"\n></iframe>`;
  }, [product?.id]);

  // Share is available for all non-draft statuses (upcoming, public, sold_out)
  const showSharePanel = Boolean(product && product.status !== 'draft');

  if (producerLoading || loading) {
    return (
      <ProducerLayout farmId={farmId || ''} producerName="Laddar..." title="Redigera produkt">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </ProducerLayout>
    );
  }

  if (!product) {
    return (
      <ProducerLayout farmId={farmId || ''} producerName={producer?.name} title="Produkt ej hittad">
        <div className="py-16 text-center">
          <h1 className="mb-2 text-xl font-medium text-foreground">Produkten hittades inte</h1>
          <p className="mb-6 text-muted-foreground">Produkten du söker finns inte längre.</p>
          <Button asChild>
            <Link to={`${basePath}/products`}>Tillbaka till produkter</Link>
          </Button>
        </div>
      </ProducerLayout>
    );
  }

  const dangerZoneContent = (
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-medium">Radera produkt</p>
        <p className="text-xs text-muted-foreground">
          Produkten tas bort från Farmly och kan inte längre köpas.
        </p>
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <Button
          variant="destructive"
          size="sm"
          disabled={isDeleting}
          onClick={() => setDeleteDialogOpen(true)}
        >
          {isDeleting ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Trash2 className="mr-2 h-4 w-4" />
          )}
          Radera
        </Button>

        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Radera produkt?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                Du är på väg att radera <strong>"{product.title}"</strong>.
              </p>
              <p>
                Denna åtgärd kan inte ångras. Produkten tas bort och försvinner från alla listor.
              </p>

              <div className="pt-2">
                <Label htmlFor="delete-confirm" className="text-foreground">
                  Skriv <strong>RADERA</strong> för att bekräfta
                </Label>
                <Input
                  id="delete-confirm"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value.toUpperCase())}
                  placeholder="RADERA"
                  className="mt-2"
                  autoComplete="off"
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteConfirmText('')}>
              Avbryt
            </AlertDialogCancel>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteConfirmText !== 'RADERA' || isDeleting}
            >
              {isDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Radera permanent
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );

  return (
    <ProducerLayout
      farmId={farmId || ''}
      producerName={producer?.name}
      title="Redigera produkt"
      maxWidth="xl"
    >
      {farmId && productId && (
        <ProductForm
          mode="edit"
          producerId={farmId}
          productId={productId}
          product={product}
          shopifyConfigured={false}
          syncing={null}
          isSaving={isSaving}
          onSave={handleSave}
          onCancel={handleCancel}
          isPublishBlocked={isPublishBlocked}
          dangerZone={dangerZoneContent}
          headerActions={showSharePanel && productPublicUrl ? (
            <EntityShareAction
              entityType="product"
              publicUrl={productPublicUrl}
              embedSnippet={productEmbedSnippet}
              socialAction={features.enableShareStudio && shareTarget ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  onClick={() => setShowShareStudio(true)}
                >
                  <Sparkles className="h-4 w-4" />
                  Share Studio
                </Button>
              ) : undefined}
            />
          ) : undefined}
        />
      )}

      {shareTarget && (
        <ShareStudioSheet
          open={showShareStudio}
          onOpenChange={setShowShareStudio}
          target={shareTarget}
        />
      )}
    </ProducerLayout>
  );
}